// Optimistic start-up from the persisted Firebase Auth session.
//
// Why this exists. On a launch after more than an hour away, Firebase Auth
// reports nobody as signed in until it has finished two network calls: it
// refreshes the ID token (securetoken.googleapis.com) and then re-reads the
// account (identitytoolkit `accounts:lookup`). Both calls carry an App Check
// token, so when the App Check token has also expired they first wait for a
// full reCAPTCHA Enterprise attestation plus the App Check exchange. Verified
// in @firebase/auth 1.13.1: `initializeCurrentUser` → `reloadAndSetCurrentUserOrClear`
// → `_reloadWithoutSaving` (token refresh, then `getAccountInfo`), and both
// `requestStsToken` and `_performApiRequest` build their headers through
// `_getAdditionalHeaders` → `_getAppCheckToken`, which awaits the exchange.
// That is four or more round trips in a row, and the whole UI waited behind
// them — the "slow start after a few days away" reports.
//
// The SDK already keeps the signed-in user in localStorage
// (`browserLocalPersistence`, see lib/firebase.ts). Reading that record lets
// the shell and the landing page render at once; Firebase Auth's own verdict
// (`onIdTokenChanged` in lib/auth.tsx) replaces it moments later.
//
// Security — this is UX only, in the sense of CLAUDE.md § 5.6:
//   - The claims below are DECODED, not verified. They decide which buttons
//     and tiles to draw, never whether anything is allowed.
//   - No data moves on the strength of this record. Firestore does not send a
//     single request until Firebase Auth has finished initialising (its
//     credential provider holds its queue until Auth's first token callback),
//     and every Cloud Function re-verifies the caller.
//   - A revoked, disabled or deleted account resolves to "signed out" when Auth
//     finishes, and ProtectedRoute then redirects to /login.
//   - Nothing new is stored: this only reads what the SDK already persisted.

import {
  ALLOWED_EMAIL_DOMAIN,
  isCapability,
  isPrimaryRole,
  type Capability,
  type OnLogicClaims,
  type PrimaryRole,
} from '@onlogic/shared';

/**
 * The parts of the signed-in user the app reads. A Firebase `User` satisfies
 * this shape, and so does the optimistic record, which is why the auth context
 * exposes this instead of the SDK class (nothing outside lib/auth.tsx calls a
 * `User` method).
 */
export interface SessionUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

export interface PersistedSession {
  user: SessionUser;
  claims: OnLogicClaims;
}

/**
 * The localStorage key Firebase Auth uses for the signed-in user
 * (`_persistenceKeyName` in @firebase/auth: `firebase:authUser:<apiKey>:<appName>`).
 */
export function persistedSessionKey(apiKey: string, appName: string): string {
  return `firebase:authUser:${apiKey}:${appName}`;
}

/**
 * Only @onlogic.com accounts may use the app. Same test as the post-sign-in
 * check in lib/auth.tsx; the security rules enforce it again server-side.
 */
export function isAllowedEmail(email: string | null | undefined): boolean {
  return (email ?? '').toLowerCase().endsWith('@' + ALLOWED_EMAIL_DOMAIN);
}

/**
 * Turn raw ID-token claims into the app's claims shape. Unknown roles fall
 * back to `employee` and unknown capabilities are dropped, exactly as the
 * verified path has always done — both paths share this function so they
 * cannot drift.
 */
export function parseClaims(raw: Record<string, unknown>): OnLogicClaims {
  return {
    role: isPrimaryRole(raw.role) ? (raw.role as PrimaryRole) : 'employee',
    capabilities: Array.isArray(raw.capabilities)
      ? ((raw.capabilities as unknown[]).filter(isCapability) as Capability[])
      : [],
    costCenter: typeof raw.costCenter === 'string' ? raw.costCenter : undefined,
    site: typeof raw.site === 'string' ? raw.site : undefined,
    managerUid: typeof raw.managerUid === 'string' ? raw.managerUid : undefined,
  };
}

/**
 * Decode a JWT's payload without verifying it. Returns null for anything that
 * is not a three-part token with a JSON object payload. The payload is UTF-8
 * (display names can carry accents), so the bytes go through TextDecoder
 * rather than being read as Latin-1 straight out of atob().
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    const payload: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return payload && typeof payload === 'object' && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Read the persisted session, or null when there is nothing usable — no
 * record, a malformed one, a foreign email domain, an undecodable token, or a
 * deactivated role. Null means "wait for Firebase Auth as before", so every
 * failure mode degrades to the old behaviour rather than to a wrong screen.
 *
 * The token is usually expired when this runs; that is expected and fine. It
 * is only read for the claims it carried at the last refresh.
 */
export function readPersistedSession(storage: Storage, key: string): PersistedSession | null {
  let record: unknown;
  try {
    const stored = storage.getItem(key);
    if (!stored) return null;
    record = JSON.parse(stored);
  } catch {
    // Storage can throw in some privacy modes; a corrupt entry fails to parse.
    return null;
  }
  if (!record || typeof record !== 'object') return null;
  const r = record as Record<string, unknown>;

  if (typeof r.uid !== 'string' || !r.uid) return null;
  const email = typeof r.email === 'string' ? r.email : null;
  if (!isAllowedEmail(email)) return null;

  const tokens = r.stsTokenManager as Record<string, unknown> | undefined;
  const accessToken = tokens && typeof tokens.accessToken === 'string' ? tokens.accessToken : null;
  if (!accessToken) return null;
  const payload = decodeJwtPayload(accessToken);
  // The token must belong to the same account as the record around it.
  if (!payload || (payload.user_id ?? payload.sub) !== r.uid) return null;

  const claims = parseClaims(payload);
  if (claims.role === 'deactivated') return null;

  return {
    user: {
      uid: r.uid,
      email,
      displayName: typeof r.displayName === 'string' ? r.displayName : null,
      photoURL: typeof r.photoURL === 'string' ? r.photoURL : null,
    },
    claims,
  };
}

/** Copy the fields the app reads off a Firebase `User` (or any SessionUser). */
export function toSessionUser(user: SessionUser): SessionUser {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
  };
}

/** Field-by-field equality, so an unchanged user keeps its object identity. */
export function sameSessionUser(a: SessionUser | null, b: SessionUser | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.uid === b.uid &&
    a.email === b.email &&
    a.displayName === b.displayName &&
    a.photoURL === b.photoURL
  );
}

/** Value equality for claims (capabilities compared in order, as issued). */
export function sameClaims(a: OnLogicClaims | null, b: OnLogicClaims | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.role === b.role &&
    a.costCenter === b.costCenter &&
    a.site === b.site &&
    a.managerUid === b.managerUid &&
    a.capabilities.length === b.capabilities.length &&
    a.capabilities.every((c, i) => c === b.capabilities[i])
  );
}
